import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AutoComplete, Button, Cascader, Checkbox, DatePicker, Input, InputNumber, Select, Space, message } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined, CheckOutlined, CloseOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs, { type Dayjs } from "dayjs";
import { Link } from "react-router";
import { Dialog as ZhDialog } from "zhihao-ui-react";
import {
    ProductDetailTabSection,
    ProductDetailTabTable,
    emptyProductDetailColumns,
    type ProductDetailFilterValues,
    type ProductDetailTabProps
} from "@/views/pages/productManagement/ProductDetailTabPrimitives";
import { scheduleService } from "@/api/services/scheduleService";
import { outputService } from "@/api/services/outputService";
import { orderService } from "@/api/services/orderService";
import { shipService } from "@/api/services/shipService";
import { waybillService } from "@/api/services/waybillService";
import { LOG_DATE_FORMAT_END, LOG_DATE_FORMAT_START } from "@/config";
import { dateFormat, fmtNum } from "@zhihaoscm/utils";
import { areaLib } from "@/utils/areaLib";
import { useUserStore } from "@/stores/react/authStore";
import { showRequestError } from "@/utils/requestErrorMessage";
import { DELIVERY_STATE, ORDER_TYPE, RECEIVE_STATE, SCHEDULE_STATE } from "@zhihaoscm/meta";
import type { OrderVo, ScheduleDateVo, ScheduleForm, ScheduleVo, TransportOrderShip } from "@zhihaoscm/entity";
import ProductDetailMapDialog from "@/views/pages/productManagement/ProductDetailMapDialog";
import { onAddressInput, sliceGbCode } from "@/views/pages/logistics/wharf";
import { getTimeTwoMonthLater } from "@/views/pages/tradeManage/components/caclenderSchedule/tools";

export type indexProps = ProductDetailTabProps;

const CALENDAR_PAGE_SIZE = 15;

type SelectOption = { label: string; value: string; item?: any };
type AddressSuggestion = {
    value: string;
    link?: { address?: string; lonlat?: string; countyCode?: string; gbCode?: string };
};

function addMonths(value: string, months: number): Date {
    const date = new Date(value);
    date.setMonth(date.getMonth() + months);
    return date;
}

function createFallbackCalendar(start: string, end: string): ScheduleDateVo[] {
    const first = new Date(start);
    const last = addMonths(end, 2);
    const dates: ScheduleDateVo[] = [];
    for (const cursor = new Date(first); cursor <= last; cursor.setDate(cursor.getDate() + 1)) {
        dates.push({
            scheduleDate: cursor.toISOString(),
            scheduleVos: [],
            output: null as never,
            remainAvailablePickedUpTon: 0,
            scheduledTon: 0
        });
    }
    return dates;
}

/** components / caclenderTab / index：保留数据展示、编辑提交和父级事件回调。 */
export default function MigrationComponent(props: indexProps) {
    const product = props.data?.productConsignment || {};
    const [calendarData, setCalendarData] = useState<ScheduleDateVo[]>([]);
    const [calendarPage, setCalendarPage] = useState(0);
    const [expandedCalendarDays, setExpandedCalendarDays] = useState<string[]>([]);
    const [calendarEditing, setCalendarEditing] = useState(false);
    const [calendarRange, setCalendarRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
    const [clickedCalendarDate, setClickedCalendarDate] = useState("");
    const [selectedCalendarDays, setSelectedCalendarDays] = useState<string[]>([]);
    const [calendarDrafts, setCalendarDrafts] = useState<Record<string, number>>({});
    const [editingOutputDate, setEditingOutputDate] = useState<string | null>(null);
    const [editingOriginalTon, setEditingOriginalTon] = useState(0);
    const [batchSettingOpen, setBatchSettingOpen] = useState(false);
    const [batchTon, setBatchTon] = useState<number | null>(null);
    const [calendarSubmitting, setCalendarSubmitting] = useState(false);
    const [totalTon, setTotalTon] = useState<number>(0);
    const [scheduleRows, setScheduleRows] = useState<any[]>([]);
    const [scheduleTotal, setScheduleTotal] = useState(0);
    const [scheduleFilters, setScheduleFilters] = useState<ProductDetailFilterValues>({});
    const [pageable, setPageable] = useState({ page: 1, size: 10 });
    const [reloadToken, setReloadToken] = useState(0);
    const [editingSchedule, setEditingSchedule] = useState<ScheduleVo | null>(null);
    const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
    const [newSchedule, setNewSchedule] = useState({
        scheduleDate: "",
        orderId: "",
        estimatedTon: "",
        transportOrderShipId: "",
        shipId: "",
        contacts: "",
        cellphone: "",
        areaCode: [] as string[],
        detailAddress: "",
        geo: undefined as { lng: number; lat: number } | undefined
    });
    const [addressOptions, setAddressOptions] = useState<
        Array<{ value: string; label: ReactNode; suggestion: AddressSuggestion }>
    >([]);
    const addressSearchSequence = useRef(0);
    const [orderOptions, setOrderOptions] = useState<SelectOption[]>([]);
    const [waybillOptions, setWaybillOptions] = useState<SelectOption[]>([]);
    const [shipOptions, setShipOptions] = useState<SelectOption[]>([]);
    const [selectedOrder, setSelectedOrder] = useState<OrderVo | null>(null);
    const [estimatedTonError, setEstimatedTonError] = useState("");
    const [areaVersion, setAreaVersion] = useState(areaLib.version);
    const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
    const permissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const canDeal = permissions.includes("ROLE_PRODUCT_DEAL");
    const deliveryPeriod =
        product.deliveryStartTime && product.deliveryEndTime
            ? `${String(product.deliveryStartTime).slice(0, 10)} 至 ${String(product.deliveryEndTime).slice(0, 10)}`
            : "--";
    useEffect(() => {
        const unsubscribe = areaLib.subscribe(() => setAreaVersion(areaLib.version));
        return () => {
            unsubscribe();
        };
    }, []);

    const areaOptions = useMemo(() => {
        // areaLib 在数据加载完成后只更新内部对象，显式读取版本号以触发选项重建。
        void areaVersion;
        return areaLib.areaList2Options(areaLib.areaData);
    }, [areaVersion]);

    /** 旧 Vue 新增排期只允许从今天或提货开始日（两者较晚者）至今天后两个月。 */
    const isScheduleDateDisabled = (date: Dayjs) => {
        if (!date.isValid()) return true;
        const today = dayjs().startOf("day");
        const deliveryStart = product.deliveryStartTime ? dayjs(product.deliveryStartTime).startOf("day") : today;
        const start = deliveryStart.isAfter(today) ? deliveryStart : today;
        const end = dayjs(getTimeTwoMonthLater()).endOf("day");
        return date.isBefore(start, "day") || date.isAfter(end, "day");
    };

    /** 旧版详细地址通过天地图联想；搜索序号防止慢请求覆盖新输入的候选。 */
    const searchScheduleAddress = async (keyword: string) => {
        const sequence = ++addressSearchSequence.current;
        if (!keyword.trim()) {
            setAddressOptions([]);
            return;
        }
        const suggestions = (await onAddressInput(keyword.trim(), newSchedule.areaCode[2] || "")) as
            | AddressSuggestion[]
            | undefined;
        if (sequence !== addressSearchSequence.current) return;
        setAddressOptions(
            (suggestions || []).map(suggestion => ({
                value: suggestion.value,
                label: (
                    <>
                        <span>{suggestion.value}</span>
                        {!newSchedule.areaCode[2] && suggestion.link?.address ? (
                            <span className="schedule-form-address-suggestion">{suggestion.link.address}</span>
                        ) : null}
                    </>
                ),
                suggestion
            }))
        );
    };

    /** 选择 POI 时回填区域和经纬度，提交格式与 Vue 的地址联想选择保持一致。 */
    const selectScheduleAddress = (suggestion: AddressSuggestion) => {
        const [lng, lat] = (suggestion.link?.lonlat || "").split(",").map(Number);
        const areaCode = suggestion.link?.countyCode || suggestion.link?.gbCode;
        setNewSchedule(current => ({
            ...current,
            detailAddress: suggestion.value,
            areaCode: current.areaCode.length ? current.areaCode : areaCode ? sliceGbCode(areaCode) : [],
            geo: Number.isFinite(lng) && Number.isFinite(lat) ? { lng, lat } : undefined
        }));
        setAddressOptions([]);
    };

    useEffect(() => {
        let disposed = false;
        const productId = product.id;
        if (!product.deliveryStartTime || !product.deliveryEndTime) {
            setCalendarData([]);
            setTotalTon(0);
            return;
        }
        // 首次按提货周期绘制空卡片；点击日期仅刷新列表时保留已加载日历，避免选中态短暂消失。
        setCalendarData(current =>
            current.length ? current : createFallbackCalendar(product.deliveryStartTime!, product.deliveryEndTime!)
        );
        if (!productId) return;
        const beginTime = dateFormat(product.deliveryStartTime, LOG_DATE_FORMAT_START);
        const endTime = dateFormat(addMonths(product.deliveryEndTime, 2), LOG_DATE_FORMAT_END);
        setCalendarPage(0);
        void Promise.allSettled([
            scheduleService.scheduleDate({ beginTime, endTime, productId: String(productId) }),
            scheduleService.amount({ productId: String(productId) }),
            scheduleService.schedulePaging({
                page: pageable.page,
                size: pageable.size,
                productId: String(productId),
                ...scheduleFilters
            } as never)
        ])
            .then(([datesResult, amountResult, pageResult]) => {
                if (disposed) return;
                // 日历、吨数与排期列表独立容错，单一接口失败不遮蔽其他已加载区块。
                if (datesResult.status === "fulfilled") {
                    setCalendarData(
                        (datesResult.value || []).map(item => ({ ...item, scheduleVos: item.scheduleVos || [] }))
                    );
                }
                if (amountResult.status === "fulfilled") setTotalTon(Number(amountResult.value || 0));
                if (pageResult.status !== "fulfilled") {
                    setScheduleRows([]);
                    setScheduleTotal(0);
                    return;
                }
                const page = pageResult.value;
                setScheduleTotal(Number(page?.total || page?.list?.length || 0));
                setScheduleRows(
                    ((page?.list || []) as unknown as ScheduleVo[]).map((item: ScheduleVo, index: number) => {
                        const schedule = item.schedule;
                        const orderId = schedule?.orderId;
                        const customerId = schedule?.customerId;
                        const customerLabel = [schedule?.customerEnterprise?.name, schedule?.customerEnterprise?.mobile]
                            .filter(Boolean)
                            .join(" | ");
                        const shipId = item.transportOrderShip?.shipId || schedule?.shipId;
                        const shipLabel = [schedule?.shipInfo?.cnname || schedule?.shipInfo?.name, schedule?.shipId]
                            .filter(Boolean)
                            .join(" | ");
                        const address = schedule?.addressInfo;
                        const areaName = areaLib.getFullAreaName(
                            address?.province,
                            address?.city,
                            address?.district
                        );
                        const contactText = [address?.contacts, address?.cellphone].filter(Boolean).join(" ");
                        const locationText = [areaName, address?.detailAddress].filter(Boolean).join(" ");
                        // 旧 Vue 在收货信息列内附带地图图标，点击时用地址经纬度打开位置弹窗。
                        const canOpenAddressMap = Boolean(address?.geo?.lng && address?.geo?.lat);
                        const addressContent = contactText || locationText ? (
                            <div className="consignment-schedule-address">
                                <span className="consignment-schedule-address__text">
                                    <span className="consignment-schedule-address__line" title={contactText}>
                                        {contactText || "--"}
                                    </span>
                                    <span className="consignment-schedule-address__line" title={locationText}>
                                        {locationText || "--"}
                                    </span>
                                </span>
                                {canOpenAddressMap ? (
                                    <ProductDetailMapDialog
                                        inline
                                        iconOnly
                                        location={{
                                            provinceCode: address?.province,
                                            cityCode: address?.city,
                                            regionCode: address?.district,
                                            address: address?.detailAddress,
                                            geo: address?.geo
                                        }}
                                    />
                                ) : null}
                            </div>
                        ) : (
                            "--"
                        );
                        const canEdit =
                            canDeal && Number(schedule?.state) === Number(SCHEDULE_STATE.TO_BE_GENERATED.code);
                        const canDelete =
                            canEdit && Number(item.receivePayments?.[0]?.state) !== Number(RECEIVE_STATE.PAID.code);
                        return {
                            id: item.schedule?.id || `schedule-${index}`,
                            排期日期: String(item.schedule?.scheduleDate || "--").slice(0, 10),
                            排期编号: item.schedule?.id || "--",
                            订单编号: orderId ? (
                                <Link to={`/tradeManage/tradeOrder/detail?id=${orderId}&time=${Date.now()}`}>
                                    {orderId}
                                </Link>
                            ) : (
                                "--"
                            ),
                            采购用户: customerId ? (
                                <Link to={`/lianYunUser/userInfo?userId=${customerId}`}>{customerLabel || "--"}</Link>
                            ) : (
                                customerLabel || "--"
                            ),
                            "预估提货吨数(吨)": item.schedule?.estimatedTon ?? "--",
                            船舶: shipId ? (
                                <Link
                                    className="consignment-schedule-ship"
                                    to={`/logistics/findLocation/${shipId}?time=${Date.now()}`}
                                >
                                    {shipLabel || shipId}
                                </Link>
                            ) : (
                                shipLabel || "--"
                            ),
                            运单: item.schedule?.transportOrderShipId || "--",
                            收货信息: addressContent,
                            排期状态:
                                SCHEDULE_STATE.getIntroByCode(Number(item.schedule?.state)) ||
                                (item.schedule as Record<string, any>)?.stateName ||
                                "--",
                            操作:
                                canEdit || canDelete ? (
                                    <Space size={8}>
                                        {canEdit ? (
                                            <Button
                                                aria-label={`修改排期-${schedule?.id}`}
                                                disabled={banButton}
                                                size="small"
                                                type="link"
                                                onClick={() => openScheduleEdit(item)}
                                            >
                                                修改
                                            </Button>
                                        ) : null}
                                        {canDelete ? (
                                            <Button
                                                aria-label={`删除排期-${schedule?.id}`}
                                                disabled={banButton}
                                                size="small"
                                                type="link"
                                                onClick={() => void deleteSchedule(item)}
                                            >
                                                删除
                                            </Button>
                                        ) : null}
                                    </Space>
                                ) : (
                                    ""
                                )
                        };
                    })
                );
            })
            .catch(() => {
                if (disposed) return;
                setTotalTon(0);
                setScheduleRows([]);
            });
        return () => {
            disposed = true;
        };
    }, [
        product.id,
        product.deliveryStartTime,
        product.deliveryEndTime,
        scheduleFilters,
        pageable,
        reloadToken,
        canDeal,
        banButton,
        areaVersion
    ]);

    /** 删除排期后同时刷新列表和日历，保持旧 Vue 两块数据联动。 */
    const deleteSchedule = async (row: ScheduleVo) => {
        const id = row.schedule?.id;
        if (!id) return;
        try {
            await scheduleService.delete(String(id));
            message.success("删除排期成功");
            setReloadToken(value => value + 1);
        } catch (error) {
            showRequestError(error, "删除排期失败");
        }
    };

    /** 复刻 Vue 排期吨数规则：编辑可用已排期+未排期额度，新增只能用未排期额度。 */
    const getEstimatedTonError = (
        value: string,
        order: OrderVo | null = selectedOrder,
        isEdit = Boolean(editingSchedule)
    ) => {
        if (!value) return "请输入预估提货吨数";
        const ton = Number(value);
        if (!Number.isInteger(ton) || ton <= 0 || ton > 10_000_000) {
            return "请输入0-10000000的正整数";
        }
        if (!order) return "";
        const availableTon = isEdit
            ? Number(order.scheduledTon || 0) + Number(order.notScheduledTon || 0)
            : Number(order.notScheduledTon || 0);
        return ton > availableTon ? "超出可排期吨数" : "";
    };

    /** 旧 Vue 编辑入口会将排期、运单及收货地址一并回填到完整表单。 */
    const openScheduleEdit = async (row: ScheduleVo) => {
        const schedule = row.schedule;
        const address = schedule?.addressInfo || {};
        const waybill = row.transportOrderShip;
        const areaCode = [address.province, address.city, address.district].filter(Boolean).map(String);
        const waybillId = String(schedule?.transportOrderShipId || waybill?.id || "");
        const shipId = String(schedule?.shipId || waybill?.shipId || "");
        setNewSchedule({
            scheduleDate: String(schedule?.scheduleDate || "").slice(0, 10),
            orderId: String(schedule?.orderId || ""),
            estimatedTon: schedule?.estimatedTon == null ? "" : String(schedule.estimatedTon),
            transportOrderShipId: waybillId,
            shipId,
            contacts: address.contacts || "",
            cellphone: address.cellphone || "",
            areaCode,
            detailAddress: address.detailAddress || "",
            geo: address.geo
        });
        setSelectedOrder(null);
        setEstimatedTonError("");
        setOrderOptions(schedule?.orderId ? [{ value: String(schedule.orderId), label: String(schedule.orderId) }] : []);
        setEditingSchedule(row);
        setScheduleDialogOpen(true);
        if (schedule?.orderId) {
            try {
                // 与旧版 showEditData 一致，编辑时按订单 ID 查询完整订单及排期吨数。
                const order = await orderService.getVo(String(schedule.orderId));
                setSelectedOrder(order);
                setEstimatedTonError(getEstimatedTonError(String(schedule.estimatedTon || ""), order, true));
                setOrderOptions([{
                    value: String(order.order.id),
                    label: [
                        order.order.customerEnterprise?.name,
                        order.order.customerEnterprise?.mobile,
                        `${fmtNum(order.order.ton || 0)}吨`,
                        order.order.id,
                        dateFormat(order.order.createdTime)
                    ].filter(Boolean).join(" | "),
                    item: order
                }]);
            } catch {
                // 订单详情暂时不可用时仍允许查看和编辑排期已有字段。
            }
        }
        setWaybillOptions(waybillId ? [{
            value: waybillId,
            label: [waybill?.id, waybill?.goodsType || "--", waybill?.ton].filter(value => value != null).join(" | "),
            item: waybill
        }] : []);
        setShipOptions(shipId ? [{
            value: shipId,
            label: `${waybill?.shipName || "--"} | ${shipId}`,
            item: waybill
        }] : []);
        setAddressOptions([]);
        addressSearchSequence.current += 1;
    };

    /** 修改排期提交完整表单，字段结构与旧 Vue 的 ScheduleForm 一致。 */
    const submitScheduleEdit = async () => {
        const schedule = editingSchedule?.schedule;
        if (!schedule?.id || !product.id || !newSchedule.scheduleDate || !newSchedule.orderId) {
            message.error("请填写排期日期、订单编号和预估提货吨数");
            return;
        }
        const tonError = getEstimatedTonError(newSchedule.estimatedTon);
        setEstimatedTonError(tonError);
        if (tonError) {
            return;
        }
        if (newSchedule.cellphone && !/^1[3-9]\d{9}$/.test(newSchedule.cellphone.trim())) {
            message.error("请输入正确的收货人手机号");
            return;
        }
        setScheduleSubmitting(true);
        try {
            await scheduleService.update(String(schedule.id), {
                id: String(schedule.id),
                productId: String(product.id),
                orderId: newSchedule.orderId,
                scheduleDate: `${newSchedule.scheduleDate}T00:00:00`,
                estimatedTon: Number(newSchedule.estimatedTon),
                transportOrderShipId: newSchedule.transportOrderShipId,
                shipId: newSchedule.shipId,
                addressInfo: {
                    contacts: newSchedule.contacts.trim(),
                    cellphone: newSchedule.cellphone.trim(),
                    province: newSchedule.areaCode[0] || "",
                    city: newSchedule.areaCode[1] || "",
                    district: newSchedule.areaCode[2] || "",
                    areaCode: newSchedule.areaCode[2] || "",
                    detailAddress: newSchedule.detailAddress.trim(),
                    geo: newSchedule.geo
                }
            } as unknown as ScheduleForm);
            message.success("修改排期成功");
            setEditingSchedule(null);
            setScheduleDialogOpen(false);
            setReloadToken(value => value + 1);
        } catch (error) {
            showRequestError(error, "修改排期失败");
        } finally {
            setScheduleSubmitting(false);
        }
    };

    /** 表头“新增排期”提交真实新增接口，避免迁移后按钮只保留外观而没有业务事件。 */
    const submitScheduleCreate = async () => {
        if (!product.id || !newSchedule.scheduleDate || !newSchedule.orderId) {
            message.error("请填写排期日期、订单编号和预估提货吨数");
            return;
        }
        const tonError = getEstimatedTonError(newSchedule.estimatedTon, selectedOrder, false);
        setEstimatedTonError(tonError);
        if (tonError) return;
        // 日期输入框可以键入文本，提交前仍需执行与旧版日历一致的范围校验。
        if (isScheduleDateDisabled(dayjs(newSchedule.scheduleDate))) {
            message.warning("请选择今天及之后的日期新增排期");
            return;
        }
        setScheduleSubmitting(true);
        try {
            await scheduleService.add({
                productId: String(product.id),
                orderId: newSchedule.orderId.trim(),
                scheduleDate: `${newSchedule.scheduleDate}T00:00:00`,
                estimatedTon: Number(newSchedule.estimatedTon),
                transportOrderShipId: newSchedule.transportOrderShipId,
                shipId: newSchedule.shipId,
                addressInfo: {
                    contacts: newSchedule.contacts.trim(),
                    cellphone: newSchedule.cellphone.trim(),
                    province: newSchedule.areaCode[0] || "",
                    city: newSchedule.areaCode[1] || "",
                    district: newSchedule.areaCode[2] || "",
                    areaCode: newSchedule.areaCode[2] || "",
                    detailAddress: newSchedule.detailAddress.trim(),
                    geo: newSchedule.geo
                }
            } as unknown as ScheduleForm);
            message.success("新增排期成功");
            setScheduleDialogOpen(false);
            setReloadToken(value => value + 1);
        } catch (error) {
            showRequestError(error, "新增排期失败");
        } finally {
            setScheduleSubmitting(false);
        }
    };

    /** 进入旧 Vue 同款日历编辑态，并保留每一天当前产量作为取消时的草稿基线。 */
    const startCalendarEdit = () => {
        setCalendarDrafts(
            Object.fromEntries(
                calendarData.map(day => [String(day.scheduleDate).slice(0, 10), Number(day.output?.ton || 0)])
            )
        );
        setSelectedCalendarDays([]);
        setEditingOutputDate(null);
        setCalendarEditing(true);
    };

    /** 取消编辑只清理本地草稿，不产生任何接口写入。 */
    const cancelCalendarEdit = () => {
        setCalendarEditing(false);
        setSelectedCalendarDays([]);
        setEditingOutputDate(null);
        setCalendarRange(null);
    };

    /** 旧版保存整张日历：空数据不请求，日期直接使用排期接口返回的原始值。 */
    const saveCalendarOutputs = async () => {
        if (!product.id) return;
        const outputItems = calendarData.flatMap(day => {
            const dateKey = String(day.scheduleDate).slice(0, 10);
            const ton = Number(calendarDrafts[dateKey] || 0);
            return ton > 0 ? [{ id: day.output?.id || undefined, outputDate: day.scheduleDate, ton }] : [];
        });
        if (!outputItems.length) return;
        setCalendarSubmitting(true);
        try {
            await outputService.batchSave({
                productId: String(product.id),
                productType: Number(product.productType || ORDER_TYPE.CONSIGNMENT_PRODUCTS.code),
                outputItems
            });
            message.success("保存成功");
            setCalendarEditing(false);
            setSelectedCalendarDays([]);
            setEditingOutputDate(null);
            setReloadToken(value => value + 1);
        } catch (error) {
            showRequestError(error, "保存失败");
        } finally {
            setCalendarSubmitting(false);
        }
    };

    /** 当前页全选会排除今天之前的日期，与旧 Vue 的日期限制一致。 */
    const toggleCalendarPageSelection = (days: ScheduleDateVo[]) => {
        const today = dayjs().startOf("day");
        const available = days
            .map(day => String(day.scheduleDate).slice(0, 10))
            .filter(date => !dayjs(date).isBefore(today));
        const allSelected = available.length > 0 && available.every(date => selectedCalendarDays.includes(date));
        setSelectedCalendarDays(allSelected ? [] : Array.from(new Set([...selectedCalendarDays, ...available])));
    };

    /** 批量设置只写入选中日期的草稿，统一由顶部保存提交。 */
    const applyBatchTon = () => {
        if (!selectedCalendarDays.length) {
            message.warning("请先选择需要设置的日期");
            return;
        }
        if (batchTon == null || batchTon < 0) {
            message.warning("请输入预估可提吨数");
            return;
        }
        setCalendarDrafts(current => ({
            ...current,
            ...Object.fromEntries(selectedCalendarDays.map(date => [date, batchTon]))
        }));
        setBatchSettingOpen(false);
    };

    /** 旧 Vue 的关联订单下拉默认加载当前商品最近 20 条待提货订单。 */
    const loadOrderOptions = async (keyword = "") => {
        if (!product.id) return;
        try {
            const page = await orderService.orderSelector({
                keyword,
                productId: String(product.id),
                states: [2, 3],
                page: 1,
                size: 20
            });
            setOrderOptions(
                (page.list || []).map(item => ({
                    value: String(item.order.id),
                    label: [
                        item.order.customerEnterprise?.name,
                        item.order.customerEnterprise?.mobile,
                        `${fmtNum(item.order.ton || 0)}吨`,
                        item.order.id,
                        dateFormat(item.order.createdTime)
                    ]
                        .filter(Boolean)
                        .join(" | "),
                    item
                }))
            );
        } catch {
            setOrderOptions([]);
        }
    };

    /** 运单与船舶保持旧 Vue 的远程搜索及自动回填关系。 */
    const loadWaybillOptions = async (keyword: string) => {
        if (!keyword) return;
        const rows = await waybillService.getSelector({ keyword, excludeScheduled: true });
        setWaybillOptions(
            (rows || []).map((item: TransportOrderShip) => ({
                value: String(item.id),
                label: [item.id, item.goodsType || "--", item.ton].filter(value => value != null).join(" | "),
                item
            }))
        );
    };

    const loadShipOptions = async (keyword: string) => {
        if (!keyword) return;
        const page = await shipService.getShipSelector({ keyword });
        setShipOptions(
            (page.list || []).map(item => ({
                value: String(item.id),
                label: `${item.cnname || item.name || "--"} | ${item.id}`,
                item
            }))
        );
    };

    const filteredCalendarData = useMemo(() => {
        if (!calendarRange?.[0] || !calendarRange[1]) return calendarData;
        const begin = calendarRange[0].startOf("day");
        const end = calendarRange[1].endOf("day");
        return calendarData.filter(day => {
            const value = dayjs(day.scheduleDate);
            return !value.isBefore(begin) && !value.isAfter(end);
        });
    }, [calendarData, calendarRange]);

    const visibleCalendarData = useMemo(
        () => filteredCalendarData.slice(calendarPage * CALENDAR_PAGE_SIZE, (calendarPage + 1) * CALENDAR_PAGE_SIZE),
        [filteredCalendarData, calendarPage]
    );
    const calendarPageCount = Math.max(1, Math.ceil(filteredCalendarData.length / CALENDAR_PAGE_SIZE));
    const selectedScheduleDay = calendarData.find(
        day => String(day.scheduleDate).slice(0, 10) === newSchedule.scheduleDate
    );
    const selectedWaybill = waybillOptions.find(item => item.value === newSchedule.transportOrderShipId)?.item as
        | TransportOrderShip
        | undefined;

    /** 普通查看态点击日历卡片时，按旧版高亮该日并联动筛选下方排期列表。 */
    const selectCalendarDay = (day: ScheduleDateVo) => {
        if (calendarEditing) return;
        const outputDate = String(day.scheduleDate).slice(0, 10);
        setClickedCalendarDate(outputDate);
        setScheduleFilters({
            beginTime: dateFormat(day.scheduleDate, LOG_DATE_FORMAT_START),
            endTime: dateFormat(day.scheduleDate, LOG_DATE_FORMAT_END)
        });
    };
    return (
        <div className="product-detail-tab-content calender-tab">
            <ProductDetailTabSection title="提货信息">
                <div className="product-detail-delivery-metrics">
                    <div className="product-detail-delivery-metric">
                        <span>提货周期</span>
                        <div className="product-detail-delivery-value">{deliveryPeriod}</div>
                    </div>
                    <div className="product-detail-delivery-metric">
                        <span>提货地点</span>
                        <div className="product-detail-delivery-value">
                            <ProductDetailMapDialog inline location={product} />
                        </div>
                    </div>
                </div>
            </ProductDetailTabSection>
            <ProductDetailTabSection title="排期日历" className="product-detail-tab-calendar">
                <div className="product-detail-calendar-header">
                    <div className="product-detail-calendar-actions">
                        {calendarEditing ? (
                            <>
                                <Button
                                    type="primary"
                                    size="middle"
                                    onClick={() => toggleCalendarPageSelection(visibleCalendarData)}
                                >
                                    全选
                                </Button>
                                <Button
                                    type="primary"
                                    size="middle"
                                    onClick={() => {
                                        if (!selectedCalendarDays.length) {
                                            message.warning("请先选择需要设置的日期");
                                            return;
                                        }
                                        setBatchTon(null);
                                        setBatchSettingOpen(true);
                                    }}
                                >
                                    批量设置
                                </Button>
                            </>
                        ) : canDeal && !banButton ? (
                            <Button type="primary" size="middle" onClick={startCalendarEdit}>
                                修改
                            </Button>
                        ) : null}
                        <DatePicker.RangePicker
                            className="product-detail-calendar-date-range"
                            format="YYYY-MM-DD"
                            placeholder={["开始时间", "结束时间"]}
                            separator="-"
                            value={calendarRange}
                            onChange={value => setCalendarRange(value as [Dayjs | null, Dayjs | null] | null)}
                        />
                        <Button icon={<SearchOutlined />} type="primary" size="middle">
                            搜索
                        </Button>
                    </div>
                    <div className="product-detail-calendar-actions">
                        {calendarEditing ? (
                            <>
                                <Button disabled={calendarSubmitting} size="middle" onClick={cancelCalendarEdit}>
                                    取消
                                </Button>
                                <Button
                                    loading={calendarSubmitting}
                                    type="primary"
                                    size="middle"
                                    onClick={() => void saveCalendarOutputs()}
                                >
                                    保存
                                </Button>
                            </>
                        ) : null}
                        <Button
                            type="primary"
                            ghost
                            size="middle"
                            icon={<ArrowLeftOutlined />}
                            disabled={calendarPage <= 0}
                            onClick={() => setCalendarPage(page => Math.max(0, page - 1))}
                        >
                            上一页
                        </Button>
                        <Button
                            type="primary"
                            ghost
                            size="middle"
                            disabled={calendarPage >= calendarPageCount - 1}
                            onClick={() => setCalendarPage(page => Math.min(calendarPageCount - 1, page + 1))}
                        >
                            下一页 <ArrowRightOutlined />
                        </Button>
                    </div>
                </div>
                <div className="product-detail-calendar-grid">
                    {visibleCalendarData.map(day => {
                        const date = new Date(day.scheduleDate);
                        const hasSchedule = Boolean(day.scheduleVos?.length);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const expired = date.getTime() < today.getTime();
                        const outputDate = String(day.scheduleDate).slice(0, 10);
                        const estimatedTon = calendarDrafts[outputDate] ?? Number(day.output?.ton || 0);
                        return (
                            <div
                                aria-label={`排期日期-${outputDate}`}
                                className={`product-detail-calendar-day${expired ? " is-expired" : ""}${
                                    !calendarEditing && clickedCalendarDate === outputDate ? " is-selected" : ""
                                }`}
                                key={day.scheduleDate}
                                role="button"
                                tabIndex={0}
                                onClick={event => {
                                    if ((event.target as HTMLElement).closest("button, input, .ant-checkbox-wrapper")) return;
                                    selectCalendarDay(day);
                                }}
                                onKeyDown={event => {
                                    if (event.key !== "Enter" && event.key !== " ") return;
                                    event.preventDefault();
                                    selectCalendarDay(day);
                                }}
                            >
                                <div className="product-detail-calendar-date">
                                    {calendarEditing && !expired ? (
                                        <Checkbox
                                            aria-label={`选择排期日期-${outputDate}`}
                                            checked={selectedCalendarDays.includes(outputDate)}
                                            onChange={event =>
                                                setSelectedCalendarDays(current =>
                                                    event.target.checked
                                                        ? Array.from(new Set([...current, outputDate]))
                                                        : current.filter(value => value !== outputDate)
                                                )
                                            }
                                        />
                                    ) : null}
                                    <strong>{dateFormat(day.scheduleDate)}</strong>
                                    <span>{date.toLocaleDateString("zh-CN", { weekday: "short" })}</span>
                                    <span className="product-detail-calendar-scheduled">
                                        <span>已排</span>
                                        <strong>{fmtNum(day.scheduledTon || 0)}</strong>吨
                                    </span>
                                </div>
                                <div
                                    className={`product-detail-calendar-estimate${
                                        !expired &&
                                        (hasSchedule ? Number(day.remainAvailablePickedUpTon || 0) : estimatedTon) > 0
                                            ? " is-active"
                                            : ""
                                    }${editingOutputDate === outputDate ? " is-editing" : ""}`}
                                >
                                    <span>{expired ? "不可提货" : hasSchedule && editingOutputDate !== outputDate ? "剩余可提" : "预估可提"}</span>
                                    {!expired && editingOutputDate === outputDate ? (
                                        <InputNumber
                                            aria-label={`预估可提吨数-${outputDate}`}
                                            className="product-detail-calendar-output-input"
                                            min={0}
                                            precision={0}
                                            value={estimatedTon}
                                            onChange={value =>
                                                setCalendarDrafts(current => ({
                                                    ...current,
                                                    [outputDate]: Number(value || 0)
                                                }))
                                            }
                                            onPressEnter={() => setEditingOutputDate(null)}
                                        />
                                    ) : !expired ? (
                                        <strong>
                                            {fmtNum(hasSchedule ? day.remainAvailablePickedUpTon || 0 : estimatedTon)}{" "}
                                            <span className="product-detail-calendar-unit">吨</span>
                                        </strong>
                                    ) : null}
                                    {calendarEditing && !expired && editingOutputDate === outputDate ? (
                                        <span className="product-detail-calendar-edit-actions">
                                            <button
                                                aria-label={`取消单日预估可提-${outputDate}`}
                                                type="button"
                                                onClick={() => {
                                                    setCalendarDrafts(current => ({ ...current, [outputDate]: editingOriginalTon }));
                                                    setEditingOutputDate(null);
                                                }}
                                            >
                                                <CloseOutlined />
                                            </button>
                                            <button
                                                aria-label={`确认单日预估可提-${outputDate}`}
                                                type="button"
                                                onClick={() => setEditingOutputDate(null)}
                                            >
                                                <CheckOutlined />
                                            </button>
                                        </span>
                                    ) : calendarEditing && !expired ? (
                                        <button
                                            aria-label={`编辑预估可提-${outputDate}`}
                                            className="product-detail-calendar-edit"
                                            type="button"
                                            onClick={() => {
                                                setEditingOriginalTon(estimatedTon);
                                                setEditingOutputDate(outputDate);
                                            }}
                                        >
                                            <i aria-hidden="true" className="iconfont icon-edit" />
                                        </button>
                                    ) : null}
                                </div>
                                {hasSchedule ? (
                                    <div className={`product-detail-calendar-schedules${expandedCalendarDays.includes(outputDate) ? " is-expanded" : ""}`}>
                                        {(expandedCalendarDays.includes(outputDate) ? day.scheduleVos : day.scheduleVos.slice(0, 3)).map(
                                            (scheduleVo, index) => {
                                                const schedule = scheduleVo.schedule;
                                                const delivery = scheduleVo.delivery;
                                                const scheduleId =
                                                    schedule?.newDataFlag === 1 && delivery?.id ? delivery.id : schedule?.id;
                                                const estimatedTon =
                                                    schedule?.newDataFlag === 1 && delivery
                                                        ? delivery.estimatedTon
                                                        : schedule?.estimatedTon;
                                                const state =
                                                    schedule?.productType === ORDER_TYPE.PICKING_UP_MISSING_PRODUCTS.code
                                                        ? SCHEDULE_STATE.getIntroByCode(schedule?.state)
                                                        : delivery?.state
                                                          ? DELIVERY_STATE.getIntroByCode(delivery.state)
                                                          : SCHEDULE_STATE.getIntroByCode(schedule?.state);
                                                return (
                                                    <div className="product-detail-calendar-schedule" key={schedule?.id || index}>
                                                        <div className="product-detail-calendar-schedule-company">
                                                            <span className="product-detail-calendar-schedule-number">{index + 1}</span>
                                                            <span className="product-detail-calendar-schedule-customer">
                                                                <span>{schedule?.customerEnterprise?.name}</span>
                                                                {schedule?.customerEnterprise?.name ? (
                                                                    <i aria-hidden="true" className="product-detail-calendar-schedule-divider" />
                                                                ) : null}
                                                                <span>{schedule?.customerEnterprise?.mobile}</span>
                                                            </span>
                                                            <span className="product-detail-calendar-schedule-state">{state}</span>
                                                        </div>
                                                        <div className="product-detail-calendar-schedule-details">
                                                            {scheduleId ? (
                                                                <span className="product-detail-calendar-schedule-detail">
                                                                    <span className="product-detail-calendar-schedule-label">排期</span>
                                                                    <span className="product-detail-calendar-schedule-value">{scheduleId}</span>
                                                                </span>
                                                            ) : null}
                                                            {schedule?.shipInfo?.cnname || schedule?.shipInfo?.name ? (
                                                                <span className="product-detail-calendar-schedule-detail">
                                                                    <span className="product-detail-calendar-schedule-label">船舶</span>
                                                                    <span className="product-detail-calendar-schedule-value">
                                                                        {schedule.shipInfo.cnname || schedule.shipInfo.name}
                                                                    </span>
                                                                </span>
                                                            ) : null}
                                                            {schedule?.transportOrderShipId ? (
                                                                <span className="product-detail-calendar-schedule-detail">
                                                                    <span className="product-detail-calendar-schedule-label">运单</span>
                                                                    <span className="product-detail-calendar-schedule-value">
                                                                        {schedule.transportOrderShipId}
                                                                    </span>
                                                                </span>
                                                            ) : null}
                                                            {estimatedTon != null ? (
                                                                <span className="product-detail-calendar-schedule-detail">
                                                                    {schedule?.newDataFlag === 1 ? null : (
                                                                        <span className="product-detail-calendar-schedule-label">预估提货</span>
                                                                    )}
                                                                    <span className="product-detail-calendar-schedule-value">
                                                                        {fmtNum(estimatedTon)}吨
                                                                    </span>
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        )}
                                        {day.scheduleVos.length > 3 ? (
                                            <button
                                                className="product-detail-calendar-show-more"
                                                type="button"
                                                onClick={() =>
                                                    setExpandedCalendarDays(current =>
                                                        current.includes(outputDate)
                                                            ? current.filter(value => value !== outputDate)
                                                            : [...current, outputDate]
                                                    )
                                                }
                                            >
                                                {expandedCalendarDays.includes(outputDate) ? "收起" : "展开更多"}
                                            </button>
                                        ) : null}
                                    </div>
                                ) : null}
                                {!calendarEditing && clickedCalendarDate === outputDate ? (
                                    <span
                                        aria-label={`已选排期日期-${outputDate}`}
                                        className="product-detail-calendar-selected"
                                        role="img"
                                    >
                                        {/* 复用 Element Plus Select 图标路径，确保右下角选中标记与旧版一致。 */}
                                        <svg aria-hidden="true" viewBox="0 0 1024 1024">
                                            <path
                                                d="M77.248 415.04a64 64 0 0 1 90.496 0l226.304 226.304L846.528 188.8a64 64 0 1 1 90.56 90.496l-543.04 543.04-316.8-316.8a64 64 0 0 1 0-90.496"
                                                fill="currentColor"
                                            />
                                        </svg>
                                    </span>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
                {!calendarData.length && <div className="product-detail-tab-empty">暂无排期</div>}
            </ProductDetailTabSection>
            <ProductDetailTabSection title="排期列表">
                <div className="product-detail-calendar-total">
                    总排期吨数（吨）<strong>{fmtNum(totalTon)}</strong>
                </div>
                <ProductDetailTabTable
                    ariaLabel="排期列表"
                    columns={emptyProductDetailColumns([
                        "排期日期",
                        "排期编号",
                        "订单编号",
                        "采购用户",
                        "预估提货吨数(吨)",
                        "船舶",
                        "运单",
                        "收货信息",
                        "排期状态",
                        ...(scheduleRows.some(row => Boolean(row.操作)) ? ["操作"] : [])
                    ])}
                    data={scheduleRows}
                    current={pageable.page}
                    pageSize={pageable.size}
                    total={scheduleTotal}
                    onSearch={values => {
                        // 列表筛选不影响日历条件，但分页需回到第一页。
                        setPageable(current => ({ ...current, page: 1 }));
                        setScheduleFilters(values);
                    }}
                    onPageChange={(page, size) => setPageable({ page, size })}
                    onCreate={
                        canDeal && !banButton
                            ? () => {
                                  setNewSchedule({
                                      scheduleDate:
                                          clickedCalendarDate && !dayjs(clickedCalendarDate).isBefore(dayjs(), "day")
                                              ? clickedCalendarDate
                                              : "",
                                      orderId: "",
                                      estimatedTon: "",
                                      transportOrderShipId: "",
                                      shipId: "",
                                      contacts: "",
                                      cellphone: "",
                                      areaCode: [],
                                      detailAddress: "",
                                      geo: undefined
                                  });
                                  addressSearchSequence.current += 1;
                                  setAddressOptions([]);
                                  setSelectedOrder(null);
                                  setEstimatedTonError("");
                                  setOrderOptions([]);
                                  setWaybillOptions([]);
                                  setShipOptions([]);
                                  void loadOrderOptions();
                                  setEditingSchedule(null);
                                  setScheduleDialogOpen(true);
                              }
                            : undefined
                    }
                />
            </ProductDetailTabSection>
            <ZhDialog
                open={scheduleDialogOpen}
                title={editingSchedule ? "修改排期" : "新增排期"}
                width="920px"
                onCancel={() => {
                    if (!scheduleSubmitting) {
                        setScheduleDialogOpen(false);
                        setEditingSchedule(null);
                    }
                }}
                footer={
                    <Space>
                        <Button
                            disabled={scheduleSubmitting}
                            onClick={() => {
                                setScheduleDialogOpen(false);
                                setEditingSchedule(null);
                            }}
                        >
                            取消
                        </Button>
                        <Button
                            loading={scheduleSubmitting}
                            type="primary"
                            onClick={() =>
                                void (editingSchedule ? submitScheduleEdit() : submitScheduleCreate())
                            }
                        >
                            提交
                        </Button>
                    </Space>
                }
            >
                <div className="schedule-form">
                    <div className="section-title schedule-form-title">排期信息</div>
                    <div className="schedule-form-grid">
                        <label className="schedule-form-item is-required">
                            <span>排期日期</span>
                            <DatePicker
                                aria-label={`${editingSchedule ? "修改" : "新增"}排期日期`}
                                className="schedule-form-date-picker"
                                format="YYYY-MM-DD"
                                disabledDate={editingSchedule ? undefined : isScheduleDateDisabled}
                                placeholder="请选择排期日期"
                                value={newSchedule.scheduleDate ? dayjs(newSchedule.scheduleDate) : null}
                                onChange={value =>
                                    setNewSchedule(current => ({
                                        ...current,
                                        scheduleDate: value?.format("YYYY-MM-DD") || ""
                                    }))
                                }
                            />
                        </label>
                        <div className="schedule-form-item schedule-form-readonly">
                            <span>剩余可提货吨数</span>
                            <strong>{fmtNum(selectedScheduleDay?.remainAvailablePickedUpTon || 0)}吨</strong>
                        </div>
                        <label className="schedule-form-item schedule-form-span-2 is-required">
                            <span>关联订单</span>
                            <Select
                                aria-label={`${editingSchedule ? "修改" : "新增"}排期关联订单`}
                                suffixIcon={null}
                                filterOption={false}
                                options={orderOptions}
                                placeholder="请选择关联订单"
                                showSearch
                                value={newSchedule.orderId || undefined}
                                disabled={Boolean(editingSchedule)}
                                onSearch={value => void loadOrderOptions(value)}
                                onChange={value => {
                                    const order =
                                        (orderOptions.find(item => item.value === value)?.item as OrderVo) || null;
                                    setNewSchedule(current => ({ ...current, orderId: value }));
                                    setSelectedOrder(order);
                                    setEstimatedTonError(
                                        newSchedule.estimatedTon
                                            ? getEstimatedTonError(newSchedule.estimatedTon, order, false)
                                            : ""
                                    );
                                }}
                            />
                        </label>
                        <div className="schedule-form-order-card schedule-form-span-2">
                            <div>
                                <span>采购提货吨数</span>
                                <strong>{selectedOrder ? `${fmtNum(selectedOrder.order?.ton || 0)}吨` : "--"}</strong>
                            </div>
                            <div>
                                <span>采购用户</span>
                                <strong>{selectedOrder?.order?.customerEnterprise?.name || "--"}</strong>
                            </div>
                            <div>
                                <span>该订单已排期吨数</span>
                                <strong>{selectedOrder ? `${fmtNum(selectedOrder.scheduledTon || 0)}吨` : "--"}</strong>
                            </div>
                            <div>
                                <span>该订单未排期吨数</span>
                                <strong>
                                    {selectedOrder ? `${fmtNum(selectedOrder.notScheduledTon || 0)}吨` : "--"}
                                </strong>
                            </div>
                        </div>
                        <label className="schedule-form-item is-required">
                            <span>预估提货吨数</span>
                            <div className="schedule-form-control">
                                <InputNumber
                                    aria-label={`${editingSchedule ? "修改" : "新增"}排期预估提货吨数`}
                                    aria-invalid={Boolean(estimatedTonError)}
                                    status={estimatedTonError ? "error" : undefined}
                                    addonAfter="吨"
                                    min={1}
                                    precision={0}
                                    placeholder="请输入预估提货吨数"
                                    value={newSchedule.estimatedTon ? Number(newSchedule.estimatedTon) : null}
                                    onChange={value => {
                                        const ton = value == null ? "" : String(value);
                                        setNewSchedule(current => ({ ...current, estimatedTon: ton }));
                                        setEstimatedTonError(getEstimatedTonError(ton));
                                    }}
                                />
                                {estimatedTonError ? (
                                    <div className="schedule-form-item-error" role="alert">
                                        {estimatedTonError}
                                    </div>
                                ) : null}
                            </div>
                        </label>
                        <div />
                        <label className="schedule-form-item">
                            <span>关联运单</span>
                            <Select
                                aria-label={`${editingSchedule ? "修改" : "新增"}排期关联运单`}
                                suffixIcon={null}
                                filterOption={false}
                                options={waybillOptions}
                                placeholder="请选择关联运单"
                                showSearch
                                value={newSchedule.transportOrderShipId || undefined}
                                onSearch={value => void loadWaybillOptions(value)}
                                onChange={value => {
                                    const waybill = waybillOptions.find(item => item.value === value)?.item as
                                        | TransportOrderShip
                                        | undefined;
                                    const shipId = String(waybill?.shipId || "");
                                    setNewSchedule(current => ({ ...current, transportOrderShipId: value, shipId }));
                                    if (shipId)
                                        setShipOptions([
                                            {
                                                value: shipId,
                                                label: `${waybill?.shipName || "--"} | ${shipId}`,
                                                item: waybill
                                            }
                                        ]);
                                }}
                            />
                        </label>
                        <label className="schedule-form-item">
                            <span>船舶</span>
                            <Select
                                aria-label={`${editingSchedule ? "修改" : "新增"}排期船舶`}
                                suffixIcon={null}
                                disabled={Boolean(editingSchedule) || Boolean(selectedWaybill)}
                                filterOption={false}
                                options={shipOptions}
                                placeholder="请输入船舶名称或AIS号搜索"
                                showSearch
                                value={newSchedule.shipId || undefined}
                                onSearch={value => void loadShipOptions(value)}
                                onChange={value => setNewSchedule(current => ({ ...current, shipId: value }))}
                            />
                        </label>
                        <label className="schedule-form-item">
                            <span>收货人</span>
                            <Input
                                aria-label={`${editingSchedule ? "修改" : "新增"}排期收货人`}
                                maxLength={32}
                                showCount
                                placeholder="请输入收货人"
                                value={newSchedule.contacts}
                                onChange={event =>
                                    setNewSchedule(value => ({ ...value, contacts: event.target.value }))
                                }
                            />
                        </label>
                        <label className="schedule-form-item">
                            <span>收货人手机号</span>
                            <Input
                                aria-label={`${editingSchedule ? "修改" : "新增"}排期收货手机号`}
                                placeholder="请输入收货人手机号"
                                value={newSchedule.cellphone}
                                onChange={event =>
                                    setNewSchedule(value => ({ ...value, cellphone: event.target.value }))
                                }
                            />
                        </label>
                        <label className="schedule-form-item schedule-form-span-2">
                            <span>收货地址</span>
                            <div className="schedule-form-address">
                                <Cascader
                                    aria-label={`${editingSchedule ? "修改" : "新增"}排期收货地区`}
                                    options={areaOptions}
                                    placeholder="请选择省市区"
                                    value={newSchedule.areaCode}
                                    onChange={value =>
                                        setNewSchedule(current => ({ ...current, areaCode: value.map(String) }))
                                    }
                                />
                                <AutoComplete
                                    aria-label={`${editingSchedule ? "修改" : "新增"}排期详细地址`}
                                    maxLength={200}
                                    options={addressOptions}
                                    placeholder="请输入详细地址"
                                    value={newSchedule.detailAddress}
                                    onChange={value => setNewSchedule(current => ({ ...current, detailAddress: value }))}
                                    onSearch={value => void searchScheduleAddress(value)}
                                    onSelect={(_, option) => selectScheduleAddress(option.suggestion)}
                                />
                            </div>
                        </label>
                    </div>
                </div>
            </ZhDialog>
            <ZhDialog
                open={batchSettingOpen}
                title="批量设置"
                width="520px"
                onCancel={() => setBatchSettingOpen(false)}
                footer={
                    <Space>
                        <Button onClick={() => setBatchSettingOpen(false)}>取消</Button>
                        <Button type="primary" onClick={applyBatchTon}>
                            确认
                        </Button>
                    </Space>
                }
            >
                <label className="schedule-batch-setting">
                    <span>预估可提吨数</span>
                    <InputNumber
                        aria-label="批量预估可提吨数"
                        addonAfter="吨"
                        min={0}
                        precision={0}
                        value={batchTon}
                        onChange={setBatchTon}
                    />
                </label>
            </ZhDialog>
            {props.children}
        </div>
    );
}
